import {
  ChangeDetectionStrategy,
  Component,
  signal,
  WritableSignal,
  output,
  computed,
  input,
  linkedSignal,
  untracked,
} from '@angular/core';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { SlicePipe } from '@angular/common';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { TranslatePipe } from '@ngx-translate/core';
import { IconUIComponent } from '../../../atoms/icon/icon.component';
import { SwitchToggleFieldComponent } from '../../../atoms/switch-toggle-field/switch-toggle-field.component';
import { IconsInitConfigType } from '../../../../constants';

export const TableColumnsMode = { Compact: 'compact', Advanced: 'advanced' } as const;
export type TableColumnsMode = (typeof TableColumnsMode)[keyof typeof TableColumnsMode];

export interface TableSettingsColumnConfig {
  name?: string;
  icon?: IconsInitConfigType;
}

@Component({
  selector: 'fr8bot-ui-table-settings',
  templateUrl: './table-settings.component.html',
  styleUrl: './table-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SwitchToggleFieldComponent,
    TranslatePipe,
    CdkOverlayOrigin,
    IconUIComponent,
    CdkConnectedOverlay,
    CdkDrag,
    CdkDragHandle,
    CdkDropList,
    SlicePipe,
  ],
})
export class TableSettingsUIComponent {
  readonly compactColumnsConfig = input<readonly string[]>([]);
  readonly fullColumnsConfig = input<readonly string[]>([]);
  readonly tableConfig = input<Record<string, TableSettingsColumnConfig> | null>(null);
  readonly canChangeColumnsOrder = input(false);
  readonly visibleColumns = input<string[]>([]);
  /** Leave unset to let the component derive the mode once from the first visible columns. */
  readonly mode = input<TableColumnsMode>();
  readonly isStatusLinesEnabled = input(false);

  readonly columnsChange = output<string[]>();
  readonly statusLinesChange = output<boolean>();
  readonly modeChange = output<TableColumnsMode>();
  readonly overlayClosedEmitter = output<void>();

  private readonly visibleColumnsState = linkedSignal(() => [...this.visibleColumns()]);

  protected readonly tableColumnMode = linkedSignal<TableColumnsMode | undefined, TableColumnsMode>(
    {
      source: this.mode,
      computation: (mode, previous) =>
        mode ?? previous?.value ?? untracked(() => this.getTableColumnsMode()),
    },
  );
  /**
   * The order the overlay lists the current mode's columns in: the table's visible order in the
   * slots of the visible columns, hidden ones where the config puts them. Kept while the host
   * only echoes a toggle back, rebuilt when the mode changes or a different order arrives.
   */
  protected readonly columnOrder = linkedSignal<
    { visible: readonly string[]; config: readonly string[] },
    string[]
  >({
    source: () => ({ visible: this.visibleColumns(), config: this.modeConfig() }),
    computation: ({ visible, config }, previous) => {
      const kept = previous?.source.config === config ? previous.value : null;

      return kept && followsOrder(kept, visible) ? kept : buildColumnOrder(visible, config);
    },
  });
  protected tableSettingsIsOpen: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly selectedColumns = computed(() => this.visibleColumnsState().slice(0, -1));
  protected readonly isStatusLinesOn = linkedSignal(() => this.isStatusLinesEnabled());

  protected onStatusLinesToggle(isEnabled: boolean): void {
    this.isStatusLinesOn.set(isEnabled);
    this.statusLinesChange.emit(isEnabled);
  }

  public toggleSelectedColumn(column: string): void {
    const visible = this.visibleColumnsState();
    const lastColumn = visible[visible.length - 1]!;
    const columnsWithoutLast = visible.slice(0, -1);

    const isSelected = columnsWithoutLast.includes(column);

    const selectedColumns = isSelected
      ? columnsWithoutLast.filter((col) => col !== column)
      : this.columnOrder().filter((col) => col === column || columnsWithoutLast.includes(col));

    this.visibleColumnsState.set([...selectedColumns, lastColumn]);

    this.emitColumnsChange();
  }

  public isColumnSelected(column: string): boolean {
    return this.visibleColumnsState().includes(column);
  }

  public fullModeSelected(): void {
    this.selectMode('advanced', this.fullColumnsConfig());
  }

  public compactModeSelected(): void {
    this.selectMode('compact', this.compactColumnsConfig());
  }

  public onToggleTableSettings(): void {
    this.tableSettingsIsOpen.set(!this.tableSettingsIsOpen());
  }

  public onDetach(): void {
    this.tableSettingsIsOpen.set(false);
  }

  public onBackdropClick(): void {
    this.tableSettingsIsOpen.set(false);
    this.overlayClosedEmitter.emit();
  }

  public onColumnDrop(event: CdkDragDrop<string[]>): void {
    if (!this.canChangeColumnsOrder()) {
      return;
    }

    const order = [...this.columnOrder()];
    moveItemInArray(order, event.previousIndex, event.currentIndex);
    this.columnOrder.set(order);

    const visible = this.visibleColumnsState();
    const lastColumn = visible[visible.length - 1]!;

    const reorderedVisible = order.filter((col) => visible.includes(col) && col !== lastColumn);

    this.visibleColumnsState.set([...reorderedVisible, lastColumn]);
    this.emitColumnsChange();
  }

  public trackByFn(index: number, item: string): string {
    return `${index}_${item}`;
  }

  private selectMode(mode: TableColumnsMode, modeColumns: readonly string[]): void {
    this.tableColumnMode.set(mode);
    this.modeChange.emit(mode);

    const visible = this.visibleColumnsState();
    const lastColumn = visible[visible.length - 1]!;
    const columnsWithoutLast = modeColumns.filter((col) => col !== lastColumn);

    this.visibleColumnsState.set([...columnsWithoutLast, lastColumn]);

    this.emitColumnsChange();
  }

  private modeConfig(): readonly string[] {
    return this.tableColumnMode() === TableColumnsMode.Compact
      ? this.compactColumnsConfig()
      : this.fullColumnsConfig();
  }

  private emitColumnsChange(): void {
    this.columnsChange.emit([...this.visibleColumnsState()]);
  }

  private getTableColumnsMode(): TableColumnsMode {
    const compactSet = new Set(this.compactColumnsConfig());
    const visibleWithoutLast = this.visibleColumnsState().slice(0, -1);
    const isCompact = visibleWithoutLast.every((column: string) => compactSet.has(column));

    return isCompact ? 'compact' : 'advanced';
  }
}

function buildColumnOrder(visible: readonly string[], config: readonly string[]): string[] {
  const body = config.slice(0, -1);
  const shown = visible.filter((column) => body.includes(column));
  let next = 0;

  return [
    ...body.map((column) => (shown.includes(column) ? shown[next++]! : column)),
    ...config.slice(-1),
  ];
}

function followsOrder(order: readonly string[], visible: readonly string[]): boolean {
  const positions = visible
    .filter((column) => order.includes(column))
    .map((column) => order.indexOf(column));

  return positions.every((position, index) => index === 0 || position > positions[index - 1]!);
}
